import { useMemo, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import { Column, Row, SmartTooltip, Text } from '~/commonComponents';
import { InviteProfessionalPopup } from '~/commonComponents/InviteProfessional/Popup';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import BulbIcon from '~/images/icons/BulbIcon';
import InfoIcon from '~/images/icons/Info';
import PlusIcon from '~/images/icons/PlusIcon';
import { Colors } from '~/themes/colors';
import { type IShiftDetailsFormValues } from '../ShiftDetailsForm/useShiftDetailsForm';
import { InvitedProfessionalPill } from './InvitedProfessionalPill';

interface IInviteFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  officeId: string;
}

type TProfessionalType = 'favorite' | 'recent' | 'new';

export const InviteField = ({ control, professionType, officeId }: IInviteFieldProps) => {
  const { field } = useController({ name: 'invite', control });
  const { field: inviteNamesField } = useController({ name: 'inviteNames', control });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [professionalToRemove, setProfessionalToRemove] = useState<string | null>(null);

  const selectedChildOffice = useAppSelector((state) => (state as any).user.selectedChildOffice);
  const effectiveOfficeId = officeId || selectedChildOffice?.id || '';

  const favoriteProfessionals = useAppSelector((state) => state.user.favoriteProfessionals ?? []);
  const recentProfessionals = useAppSelector((state) => state.user.recentProfessionals ?? []);
  const recommendedProfessionals = useAppSelector(
    (state) => state.user.recommendedProfessionals ?? [],
  );

  const allProfessionals = useMemo(
    () => [...favoriteProfessionals, ...recentProfessionals, ...recommendedProfessionals],
    [favoriteProfessionals, recentProfessionals, recommendedProfessionals],
  );

  const getTProfessionalType = (id: string): TProfessionalType => {
    if (favoriteProfessionals.some((p: any) => p?.id === id)) return 'favorite';
    if (recentProfessionals.some((p: any) => p?.id === id || p?.professional?.id === id))
      return 'recent';
    return 'new';
  };

  const getFullName = (id: string): string => {
    const match: any = allProfessionals.find(
      (p: any) => p?.id === id || p?.professional?.id === id,
    );
    return (
      match?.fullName ??
      match?.fullname ??
      match?.professional?.fullName ??
      match?.professional?.full_name ??
      inviteNamesField.value?.[id] ??
      ''
    );
  };

  const handleRemove = (id: string) => {
    setProfessionalToRemove(id);
  };

  const handleConfirmRemove = () => {
    if (professionalToRemove) {
      field.onChange(field.value.filter((pId: string) => pId !== professionalToRemove));
      setProfessionalToRemove(null);
    }
  };

  const handleConfirm = (ids: string[]) => {
    field.onChange(ids);
    setIsPopupOpen(false);
  };

  return (
    <>
      <Column data-testid="invite-field" gap={8}>
        <Row justifyContent="space-between" alignItems="center">
          <Row alignItems="center" gap={6}>
            <Text bold fontSize={18} color="neutral_600">
              Invite:
            </Text>
            <SmartTooltip
              position="bottom"
              content={
                <Text color="white" bold>
                  Please note, invites will not be delivered to professionals on requested dates in
                  which they are unavailable.
                </Text>
              }
            >
              <InfoIcon color={Colors.neutral_300} />
            </SmartTooltip>
          </Row>
          <Row
            alignItems="center"
            gap={6}
            onClick={() => setIsPopupOpen(true)}
            style={{ cursor: 'pointer' }}
            data-testid="invite-add-button"
          >
            <PlusIcon color={Colors.secondary_500} />
            <Text bold fontSize={18} color="secondary_500">
              Add Professionals
            </Text>
          </Row>
        </Row>

        {field.value.length === 0 && (
          <Row
            backgroundColor={Colors.primary_50}
            borderRadius={8}
            height={68}
            paddingHorizontal={16}
            data-testid="invite-tip-banner"
            alignItems="center"
            marginTop={12}
            marginBottom={8}
          >
            <Row gap={6}>
              <Row alignItems="flex-start" gap={6}>
                <BulbIcon color={Colors.secondary_500} />
                <Text bold fontSize={18} color="primary_500">
                  Tip:{' '}
                </Text>
              </Row>
              <Text fontSize={16} marginTop={2}>
                Secure coverage with ease! Shifts with invites fill 2x faster.
              </Text>
            </Row>
          </Row>
        )}

        {field.value.length > 0 && (
          <Row
            style={{ flexWrap: 'wrap', gap: 10, marginTop: 8, marginBottom: 8 }}
            data-testid="invited-professionals-list"
          >
            {[...field.value]
              .sort((a, b) => {
                const aFav = getTProfessionalType(a) === 'favorite' ? 0 : 1;
                const bFav = getTProfessionalType(b) === 'favorite' ? 0 : 1;
                return aFav - bFav;
              })
              .map((id: string) => (
                <InvitedProfessionalPill
                  key={id}
                  name={getFullName(id)}
                  type={getTProfessionalType(id)}
                  onRemove={() => handleRemove(id)}
                />
              ))}
          </Row>
        )}
      </Column>

      <InviteProfessionalPopup
        confirmButtonText="Confirm"
        isOpen={isPopupOpen}
        isRefetchJob={false}
        defaultProfessionals={field.value}
        profession={UserProfession.getCode(professionType)}
        officeId={effectiveOfficeId}
        onClose={() => setIsPopupOpen(false)}
        onConfirm={handleConfirm}
      />

      {professionalToRemove && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Remove Professional From Invite"
          description={
            <>
              Are you sure you would like to remove{' '}
              <strong>{getFullName(professionalToRemove)}</strong> from your request?
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setProfessionalToRemove(null)}
          rightButtonText="Remove"
          rightButtonAction={handleConfirmRemove}
          closePopup={() => setProfessionalToRemove(null)}
          dataTestId="remove-professional-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}
    </>
  );
};
